import { makeAutoObservable, runInAction } from 'mobx';
import type {
  PartnerAuthService,
  PartnerLoginRequest,
  PartnerSessionValidateResponse,
} from '@/core/infrastructure/services/PartnerAuthService';
import type {
  OTPAuthService,
  OTPLoginRequest,
  OTPRegisterRequest,
  OTPRequestParams,
  PhoneLookupRequest,
} from '@/core/infrastructure/services/OTPAuthService';
import type { AnonymousSessionService } from '@/core/infrastructure/services/AnonymousSessionService';
import { OTPAuthenticationUseCase } from '@/core/application/use-cases/auth/OTPAuthenticationUseCase';
import { format } from 'date-fns';

export class AuthStore {
  // State
  currentUser: User | null = null;
  isAuthenticated = false;
  isLoading = false;
  isHydrated = false;
  isSigningIn = false;
  isSigningOut = false;
  error: string | null = null;
  errors: AuthError[] = [];
  authTokens: { accessToken: string; refreshToken: string } | null = null;

  loginAuthSession: LoginAuthSession | null = null;
  // Phone authentication state
  phoneAuthSession: PhoneAuthSession | null = null;
  isPhoneAuthInProgress = false;
  isSendingOtp = false;
  isVerifyingOtp = false;

  // New authentication states
  partnerAuthSession: PartnerAuthSession | null = null;
  otpSession: OTPSession | null = null;
  anonymousSessionActive = false;

  // Loading states for new auth methods
  isValidatingPartnerSession = false;
  isRegisteringPartnerUser = false;
  isLoggingInPartnerUser = false;
  isLookingUpPhone = false;
  isRequestingOTP = false;
  isRegisteringWithOTP = false;
  isLoggingInWithOTP = false;

  // Login state
  lastLoginMethod: 'email' | 'phone' | 'partner' | 'otp' | null = null;
  rememberMe = false;

  // Session management
  sessionExpiresAt: Date | null = null;
  isSessionExpiring = false;

  // Registration data management
  registrationData: RegistrationData = {};
  isUpdatingRegistration = false;

  constructor(
    private readonly authenticateUserUseCase: OTPAuthenticationUseCase,
    private readonly partnerAuthService: PartnerAuthService,
    private readonly otpAuthService: OTPAuthService,
    private readonly anonymousSessionService: AnonymousSessionService
  ) {
    makeAutoObservable(this);
    // Avoid running client-side hydration logic during SSR. Otherwise the server can
    // render a different initial UI than the browser (causing hydration errors).
    if (typeof window !== 'undefined') {
      this.initializeAuth();
    }
  }

  // Actions

  // Computed values
  get userDisplayName(): string {
    return (this.currentUser?.first_name ?? '').split(' ')[0] ?? 'User';
  }

  get photoUrl(): string | undefined {
    return undefined;
  }

  get userId(): string | null {
    return this.currentUser?.id || null;
  }

  get userFullDisplayName(): string {
    return `${this.currentUser?.first_name} ${this.currentUser?.last_name}` || 'User';
  }

  get userEmail(): string {
    // Use root-level email (backend response) or fallback to identity.email
    return this.currentUser?.email || this.currentUser?.identity?.email || '';
  }

  get userPhoneNumber(): string {
    // Use root-level phone_number (backend response), fallback to identity.phone,
    // or partner session msisdn for partner-authenticated users
    return (
      this.currentUser?.phone_number ||
      this.currentUser?.identity?.phone ||
      this.partnerAuthSession?.userData?.msisdn ||
      ''
    );
  }

  get isKycCompleted(): boolean {
    return (
      this.currentUser?.kyc_level !== 'NONE'
    );
  }

  get isEligibleForCredit(): boolean {
    if (!this.currentUser) return false;

    return (
      this.currentUser.kyc_level === 'FULL' 
    );
  }

  get hasValidTokens(): boolean {
    return this.authTokens !== null;
  }

  get isSignedIn(): boolean {
    return this.isAuthenticated && this.currentUser !== null;
  }

  get hasErrors(): boolean {
    return this.errors.length > 0 || this.error !== null;
  }

  get primaryError(): string | null {
    if (this.error) return this.error;
    if (this.errors.length > 0) return this.errors[0]?.message ?? null;
    return null;
  }

  get isPhoneAuthExpired(): boolean {
    if (!this.phoneAuthSession) return false;
    return new Date() > this.phoneAuthSession.expiresAt;
  }

  get phoneAuthTimeRemaining(): number {
    if (!this.phoneAuthSession) return 0;
    const remaining =
      this.phoneAuthSession.expiresAt.getTime() - new Date().getTime();
    return Math.max(0, Math.floor(remaining / 1000));
  }

  get canResendOTP(): boolean {
    return (
      this.phoneAuthSession !== null &&
      !this.isSendingOtp &&
      !this.isPhoneAuthExpired
    );
  }

  // Helper methods

  get isSessionNearExpiry(): boolean {
    if (!this.sessionExpiresAt) return false;
    const timeRemaining =
      this.sessionExpiresAt.getTime() - new Date().getTime();
    return timeRemaining < 15 * 60 * 1000; // 15 minutes
  }

  // New computed values for new auth methods
  get hasPartnerSession(): boolean {
    return this.partnerAuthSession !== null;
  }

  get isPartnerSessionExpired(): boolean {
    if (!this.partnerAuthSession) return false;
    return new Date() > this.partnerAuthSession.expiresAt;
  }

  get canAutoLoginPartner(): boolean {
    return (
      this.hasPartnerSession &&
      this.partnerAuthSession!.autoLoginAvailable &&
      !this.isPartnerSessionExpired
    );
  }

  get isNewPartnerUser(): boolean {
    return (
      this.hasPartnerSession &&
      this.partnerAuthSession!.userStatus === 'new_user'
    );
  }

  get hasOTPSession(): boolean {
    return this.otpSession !== null;
  }

  get isOTPSessionExpired(): boolean {
    if (!this.otpSession) return false;
    return new Date() > this.otpSession.expiresAt;
  }

  get otpTimeRemaining(): number {
    if (!this.otpSession) return 0;
    const remaining =
      this.otpSession.expiresAt.getTime() - new Date().getTime();
    return Math.max(0, Math.floor(remaining / 1000));
  }

  get canResendOTPNew(): boolean {
    return (
      this.hasOTPSession && !this.isOTPSessionExpired && !this.isRequestingOTP
    );
  }

  get hasAnonymousSession(): boolean {
    return this.anonymousSessionActive;
  }

  get authenticationMethod(): string {
    return this.lastLoginMethod || 'none';
  }

  // Computed for checking registration completeness
  get isRegistrationComplete(): boolean {
    const required = [
      'phone',
      'email',
      'fullName',
      'dateOfBirth',
      'gender',
      'maritalStatus',
      'otpCode',
    ];
    return required.every(
      (field) => !!this.registrationData[field as keyof RegistrationData]
    );
  }

  getPayload() {
    return {
      first_name:
        this.registrationData.firstName ||
        this.registrationData.fullName?.split(' ')[0] ||
        '',
      last_name:
        this.registrationData.lastName ||
        this.registrationData.fullName?.split(' ').slice(1).join(' ') ||
        '',
      email: this.registrationData.email!,
      marketing_consent: this.registrationData.acceptsMarketing || false,
      whatsapp_same_number: this.registrationData.whatsappSameNumber,
      // Additional profile data
      date_of_birth: this.registrationData.dateOfBirth,
      gender: this.registrationData.gender,
      marital_status: this.registrationData.maritalStatus,
      occupation: this.registrationData.occupation,
      company: this.registrationData.company,
      salary: this.registrationData.salary,
      digital_address: this.registrationData.digitalAddress,
      residential_address: this.registrationData.residentialAddress,
      city: this.registrationData.city,
      region: this.registrationData.region,
      postal_code: this.registrationData.postalCode,
      about_us: this.registrationData.aboutUs,
      merchant: this.registrationData.merchant,
    };
  }

  isFieldMissing(field: string): boolean {
    return this.partnerAuthSession?.missingFields?.includes(field) ?? false;
  }

  // Initiate phone authentication with OTP
  async initiatePhoneAuth(phoneNumber: string): Promise<boolean> {
    try {
      this.isSendingOtp = true;
      this.clearErrors();

      if (!this.isValidPhoneNumber(phoneNumber)) {
        throw new Error('Invalid phone number format');
      }

      // Send OTP
      await this.sendOTP(phoneNumber, 'LOGIN');

      // Create phone auth session
      runInAction(() => {
        this.phoneAuthSession = {
          phoneNumber,
          otpSent: true,
          otpVerified: false,
          expiresAt: new Date(Date.now() + 10 * 60 * 1000), // 10 minutes
          attempts: 0,
          maxAttempts: 3,
        };
        this.isPhoneAuthInProgress = true;
      });

      return true;
    } catch (error: any) {
      this.addError({ message: error.message || 'Failed to send OTP' });
      return false;
    } finally {
      this.isSendingOtp = false;
    }
  }

  // Complete phone authentication with OTP
  async completePhoneAuth(otp: string): Promise<boolean> {
    if (!this.phoneAuthSession) {
      throw new Error('No active phone authentication session');
    }

    try {
      this.isVerifyingOtp = true;
      this.clearErrors();

      // Check if session is expired
      if (new Date() > this.phoneAuthSession.expiresAt) {
        throw new Error('OTP session expired. Please request a new OTP.');
      }

      // Check attempt limits
      if (this.phoneAuthSession.attempts >= this.phoneAuthSession.maxAttempts) {
        throw new Error(
          'Maximum OTP attempts exceeded. Please try again later.'
        );
      }

      // Increment attempts
      runInAction(() => {
        this.phoneAuthSession!.attempts += 1;
      });

      // Verify OTP
      const isValid = await this.verifyOTP(
        this.phoneAuthSession.phoneNumber,
        otp
      );

      if (isValid) {
        runInAction(() => {
          this.phoneAuthSession!.otpVerified = true;
          this.isAuthenticated = true;
          this.lastLoginMethod = 'phone';
          this.sessionExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);
        });

        // Clear phone auth session
        this.clearPhoneAuthSession();
        return true;
      } else {
        if (
          this.phoneAuthSession.attempts >= this.phoneAuthSession.maxAttempts
        ) {
          this.clearPhoneAuthSession();
          throw new Error('Maximum OTP attempts exceeded');
        }
        throw new Error('Invalid OTP. Please try again.');
      }
    } catch (error: any) {
      this.addError({ message: error.message || 'OTP verification failed' });
      return false;
    } finally {
      this.isVerifyingOtp = false;
    }
  }

  // Legacy method for backward compatibility
  // async signInLegacy(email: string, password: string): Promise<void> {
  //     this.setSigningIn(true);
  //     this.setError(null);
  //
  //     try {
  //         const result = await this.authenticateUserUseCase.signIn(email, password);
  //
  //         runInAction(() => {
  //             this.currentUser = UserDto.fromDomain(result.user);
  //             this.authTokens = result.authTokens;
  //             this.isAuthenticated = true;
  //         });
  //
  //         // Store tokens in localStorage for persistence
  //         this.persistAuthTokens(result.authTokens);
  //     } catch (error) {
  //         this.setError(error instanceof Error ? error.message : 'Failed to sign in');
  //         throw error;
  //     } finally {
  //         this.setSigningIn(false);
  //     }
  // }

  // async signOut(): Promise<void> {
  //     this.setSigningOut(true);
  //     this.setError(null);
  //
  //     try {
  //         await this.authenticateUserUseCase.signOut();
  //
  //         runInAction(() => {
  //             this.currentUser = null;
  //             this.authTokens = null;
  //             this.isAuthenticated = false;
  //         });
  //
  //         // Clear persisted tokens
  //         this.clearPersistedAuthTokens();
  //     } catch (error) {
  //         this.setError(error instanceof Error ? error.message : 'Failed to sign out');
  //     } finally {
  //         this.setSigningOut(false);
  //     }
  // }

  async getCurrentUser(): Promise<void> {
    this.setLoading(true);

    try {
      const user = await this.authenticateUserUseCase.getCurrentUser();

      runInAction(() => {
        if (user) {
          this.currentUser = user;
          this.isAuthenticated = true;
        } else {
          this.currentUser = null;
          this.isAuthenticated = false;
          this.authTokens = null;
          this.clearPersistedAuthTokens();
        }
      });
    } catch (_error) {
      runInAction(() => {
        this.currentUser = null;
        this.isAuthenticated = false;
        this.authTokens = null;
      });
      this.clearPersistedAuthTokens();
    } finally {
      this.setLoading(false);
    }
  }

  // async refreshToken(): Promise<void> {
  //   try {
  //     const newToken = await this.authenticateUserUseCase.refreshToken();
  //
  //     runInAction(() => {
  //       if (this.authTokens) {
  //         this.authTokens = {
  //           ...this.authTokens,
  //           accessToken: newToken,
  //         };
  //       }
  //     });
  //
  //     // Update persisted tokens
  //     if (this.authTokens) {
  //       this.persistAuthTokens(this.authTokens);
  //     }
  //   } catch (error) {
  //     // Token refresh failed, sign out user
  //     await this.signOut();
  //   }
  // }

  async verifyOTP(phoneNumber: string, otp: string): Promise<boolean> {
    this.setLoading(true);
    this.setError(null);

    try {
      await this.authenticateUserUseCase.validateOTP(phoneNumber, otp);

      return true;
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to verify OTP'
      );
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  async sendOTP(
    phoneNumber: string,
    purpose: 'SIGNUP' | 'LOGIN'
  ): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      await this.authenticateUserUseCase.requestOTP(phoneNumber, purpose);
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to send OTP'
      );
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  // async resetPassword(email: string): Promise<void> {
  //   this.setLoading(true);
  //   this.setError(null);
  //
  //   try {
  //     await this.authenticateUserUseCase.resetPassword(email);
  //   } catch (error) {
  //     this.setError(
  //       error instanceof Error ? error.message : 'Failed to reset password'
  //     );
  //     throw error;
  //   } finally {
  //     this.setLoading(false);
  //   }
  // }

  // Partner Authentication Methods
  async validatePartnerSession(
    sessionToken: string
  ): Promise<PartnerSessionValidateResponse> {
    this.isValidatingPartnerSession = true;
    this.clearErrors();

    try {
      const sessionData = await this.partnerAuthService.validatePartnerSession(
        sessionToken
      );

      runInAction(() => {
        this.partnerAuthSession = {
          sessionToken,
          userStatus: sessionData.user_status,
          autoLoginAvailable: sessionData.auto_login_available,
          requiresOtp: sessionData.requires_otp ?? false,
          requiresOtpReason: sessionData.requires_otp_reason,
          missingFields: sessionData.missing_fields,
          userData: sessionData.user_data,
          partnerCode: sessionData.partner_code,
          campaignId: sessionData.campaign_id,
          expiresAt: new Date(sessionData.expires_at),
        };
        this.registrationData.dateOfBirth = sessionData.user_data.date_of_birth;
        this.registrationData.gender =
          sessionData.user_data.gender?.toLowerCase();
        this.registrationData.fullName = `${sessionData.user_data.first_name} ${sessionData.user_data.last_name}`;
      });

      return sessionData;
    } catch (error) {
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to validate partner session',
      });
      throw error;
    } finally {
      this.isValidatingPartnerSession = false;
    }
  }

  async registerPartnerUser(): Promise<void> {
    const data = this.registrationData;
    if (!this.partnerAuthSession) {
      throw new Error('No active partner session');
    }
    const payload = {
      session_token: this.partnerAuthSession.sessionToken,
      additional_data: {
        email: data.email ?? '',
        region: data.region ?? '',
        employment_status: data.employmentStatus ?? '',
        occupation: data.occupation ?? '',
        gender: data.gender,
        date_of_birth: format(data.dateOfBirth ?? new Date(), 'yyyy-MM-dd'),
        whatsapp_same_number: data.whatsappSameNumber ?? true,
      },
      terms_acceptance: {
        terms_accepted: '1.0',
        privacy_policy: '1.0',
        marketing_consent: data.acceptsMarketing ?? true,
        accepted_at: new Date().toISOString(),
      },
      device_info: this.partnerAuthService.generateDeviceInfo(),
    };

    this.isRegisteringPartnerUser = true;
    this.clearErrors();

    try {
      const authResponse = await this.partnerAuthService.registerPartnerUser(
        payload as any
      );

      // Ensure server-side auth cookies are set (middleware relies on access_token cookie).
      await this.setServerSessionCookies({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        partner_code: this.partnerAuthSession?.partnerCode,
        expires_in: authResponse.expires_in,
      });

      runInAction(() => {
        this.currentUser = authResponse.user as any;
        this.isAuthenticated = true;
        this.lastLoginMethod = 'partner';
        this.sessionExpiresAt = new Date(
          Date.now() + authResponse.expires_in * 1000
        );
        this.partnerAuthSession = null; // Clear session after successful registration
      });

      this.persistAuthSession({
        user: authResponse.user,
        expiresAt: this.sessionExpiresAt!,
        method: 'partner',
      });
    } catch (error) {
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to register partner user',
      });
      throw error;
    } finally {
      this.isRegisteringPartnerUser = false;
    }
  }

  async loginPartnerUser(): Promise<void> {
    if (!this.partnerAuthSession) {
      throw new Error('No active partner session');
    }

    this.isLoggingInPartnerUser = true;
    this.clearErrors();

    try {
      const request: PartnerLoginRequest = {
        session_token: this.partnerAuthSession.sessionToken,
        device_info: this.partnerAuthService.generateDeviceInfo(),
      };

      const authResponse = await this.partnerAuthService.loginPartnerUser(
        request
      );

      // Ensure server-side auth cookies are set (middleware relies on access_token cookie).
      await this.setServerSessionCookies({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        partner_code: this.partnerAuthSession?.partnerCode,
        expires_in: authResponse.expires_in,
      });

      runInAction(() => {
        this.currentUser = authResponse.user as any;
        this.isAuthenticated = true;
        this.lastLoginMethod = 'partner';
        this.sessionExpiresAt = new Date(
          Date.now() + authResponse.expires_in * 1000
        );
        this.partnerAuthSession = null; // Clear session after successful login
      });

      this.persistAuthSession({
        user: authResponse.user,
        expiresAt: this.sessionExpiresAt!,
        method: 'partner',
      });
    } catch (error) {
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to login partner user',
      });
      throw error;
    } finally {
      this.isLoggingInPartnerUser = false;
    }
  }

  // OTP Authentication Methods
  async lookupPhone(
    phoneNumber: string
  ): Promise<{ exists: boolean; message: string }> {
    this.isLookingUpPhone = true;
    this.clearErrors();

    try {
      const validation = this.otpAuthService.validatePhoneNumber(phoneNumber);
      if (!validation.isValid) {
        throw new Error(validation.error || 'Invalid phone number');
      }

      const request: PhoneLookupRequest = {
        msisdn: validation.formattedNumber,
      };

      const result = await this.otpAuthService.lookupPhone(request);
      return result;
    } catch (error) {
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to lookup phone number',
      });
      throw error;
    } finally {
      this.isLookingUpPhone = false;
    }
  }

  async requestOTPNew(
    phoneNumber: string,
    purpose: 'SIGNUP' | 'LOGIN'
  ): Promise<void> {
    this.isRequestingOTP = true;
    this.clearErrors();

    try {
      const validation = this.otpAuthService.validatePhoneNumber(phoneNumber);
      if (!validation.isValid) {
        throw new Error(validation.error || 'Invalid phone number');
      }

      // Check rate limiting
      const canRequest = this.otpAuthService.canRequestOTP(
        validation.formattedNumber
      );
      if (!canRequest.canRequest) {
        throw new Error(
          `Please wait ${canRequest.waitTime} seconds before requesting another OTP`
        );
      }

      const params: OTPRequestParams = {
        msisdn: validation.formattedNumber,
        purpose,
      };

      const response = await this.otpAuthService.requestOTP(params);

      runInAction(() => {
        this.otpSession = {
          msisdn: validation.formattedNumber,
          requestId: response.request_id,
          purpose,
          expiresAt: new Date(Date.now() + response.expires_in * 1000),
          attempts: 0,
          maxAttempts: 3,
        };
      });

      // Record request for rate limiting
      this.otpAuthService.recordOTPRequest(validation.formattedNumber);
    } catch (error) {
      this.addError({
        message:
          error instanceof Error ? error.message : 'Failed to request OTP',
      });
      throw error;
    } finally {
      this.isRequestingOTP = false;
    }
  }

  async registerWithOTP(
    otpCode: string,
    userData: {
      first_name: string;
      last_name: string;
      email?: string;
      marketing_consent?: boolean;
    }
  ): Promise<void> {
    if (!this.otpSession || this.otpSession.purpose !== 'SIGNUP') {
      throw new Error('No active OTP signup session');
    }

    this.isRegisteringWithOTP = true;
    this.clearErrors();

    try {
      if (this.isOTPSessionExpired) {
        throw new Error('OTP session expired. Please request a new OTP.');
      }

      if (!this.otpAuthService.isValidOTP(otpCode)) {
        throw new Error('Invalid OTP format');
      }

      // Increment attempts
      runInAction(() => {
        this.otpSession!.attempts += 1;
      });

      if (this.otpSession.attempts > this.otpSession.maxAttempts) {
        this.clearOTPSession();
        throw new Error('Maximum OTP attempts exceeded');
      }

      const request: OTPRegisterRequest = {
        msisdn: this.otpSession.msisdn,
        otp_code: otpCode,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        marketing_consent: userData.marketing_consent,
        device_info: this.otpAuthService.generateDeviceInfo(),
      };

      const authResponse = await this.otpAuthService.registerWithOTP(request);

      // Ensure server-side auth cookies are set (middleware relies on access_token cookie).
      await this.setServerSessionCookies({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        expires_in: authResponse.expires_in,
      });

      runInAction(() => {
        this.currentUser = authResponse.user as any;
        this.isAuthenticated = true;
        this.lastLoginMethod = 'otp';
        this.sessionExpiresAt = new Date(
          Date.now() + authResponse.expires_in * 1000
        );
        this.otpSession = null; // Clear session after successful registration
      });

      this.persistAuthSession({
        user: authResponse.user,
        expiresAt: this.sessionExpiresAt!,
        method: 'otp',
      });
    } catch (error) {
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to register with OTP',
      });
      throw error;
    } finally {
      this.isRegisteringWithOTP = false;
    }
  }

  async loginWithOTP(otpCode: string): Promise<void> {
    if (!this.otpSession || this.otpSession.purpose !== 'LOGIN') {
      throw new Error('No active OTP login session');
    }

    this.isLoggingInWithOTP = true;
    this.clearErrors();

    try {
      if (this.isOTPSessionExpired) {
        throw new Error('OTP session expired. Please request a new OTP.');
      }

      if (!this.otpAuthService.isValidOTP(otpCode)) {
        throw new Error('Invalid OTP format');
      }

      // Increment attempts
      runInAction(() => {
        this.otpSession!.attempts += 1;
      });

      if (this.otpSession.attempts > this.otpSession.maxAttempts) {
        this.clearOTPSession();
        throw new Error('Maximum OTP attempts exceeded');
      }

      const request: OTPLoginRequest = {
        msisdn: this.otpSession.msisdn,
        otp_code: otpCode,
        device_info: this.otpAuthService.generateDeviceInfo(),
      };

      const authResponse = await this.otpAuthService.loginWithOTP(request);

      // Ensure server-side auth cookies are set (middleware relies on access_token cookie).
      await this.setServerSessionCookies({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        expires_in: authResponse.expires_in,
      });

      runInAction(() => {
        this.currentUser = authResponse.user as any;
        this.isAuthenticated = true;
        this.lastLoginMethod = 'otp';
        this.sessionExpiresAt = new Date(
          Date.now() + authResponse.expires_in * 1000
        );
        this.otpSession = null; // Clear session after successful login
      });

      this.persistAuthSession({
        user: authResponse.user,
        expiresAt: this.sessionExpiresAt!,
        method: 'otp',
      });
    } catch (error) {
      this.addError({
        message:
          error instanceof Error ? error.message : 'Failed to login with OTP',
      });
      throw error;
    } finally {
      this.isLoggingInWithOTP = false;
    }
  }

  // Anonymous Session Methods
  async createAnonymousSession(): Promise<void> {
    try {
      await this.anonymousSessionService.createAnonymousSession();
      runInAction(() => {
        this.anonymousSessionActive = true;
      });
    } catch (_error) {
      // Best-effort: anonymous session is an optimization, not a hard requirement.
    }
  }

  clearAnonymousSession(): void {
    this.anonymousSessionService.clearSession();
    runInAction(() => {
      this.anonymousSessionActive = false;
    });
  }

  async ensureAnonymousSession(): Promise<void> {
    if (!this.isAuthenticated && !this.hasAnonymousSession) {
      await this.createAnonymousSession();
    }
  }

  // Phone authentication helpers
  clearPhoneAuthSession(): void {
    runInAction(() => {
      this.phoneAuthSession = null;
      this.isPhoneAuthInProgress = false;
    });
  }

  async resendOTP(purpose: 'SIGNUP' | 'LOGIN' = 'SIGNUP'): Promise<boolean> {
    if (!this.phoneAuthSession) {
      return false;
    }

    try {
      await this.sendOTP(this.phoneAuthSession.phoneNumber, purpose);

      runInAction(() => {
        if (this.phoneAuthSession) {
          this.phoneAuthSession.expiresAt = new Date(
            Date.now() + 10 * 60 * 1000
          );
          this.phoneAuthSession.attempts = 0;
        }
      });

      return true;
    } catch (_error) {
      return false;
    }
  }

  // New session helpers
  clearPartnerAuthSession(): void {
    runInAction(() => {
      this.partnerAuthSession = null;
    });
  }

  clearOTPSession(): void {
    runInAction(() => {
      this.otpSession = null;
    });
  }

  parsePartnerTokenFromUrl(url: string): string | null {
    return this.partnerAuthService.parsePartnerToken(url);
  }

  formatPhoneNumberForDisplay(phoneNumber: string): string {
    return this.otpAuthService.formatPhoneForDisplay(phoneNumber);
  }

  validatePhoneNumberInput(phoneNumber: string): {
    isValid: boolean;
    formattedNumber: string;
    error?: string;
  } {
    return this.otpAuthService.validatePhoneNumber(phoneNumber);
  }

  formatOTPInput(input: string): string {
    return this.otpAuthService.formatOTPInput(input);
  }

  isValidOTPInput(otp: string): boolean {
    return this.otpAuthService.isValidOTP(otp);
  }

  // Enhanced sign out to clear all sessions
  async signOutComplete(): Promise<void> {
    this.setSigningOut(true);

    try {
      // Clear all authentication states
      runInAction(() => {
        this.currentUser = null;
        this.authTokens = null;
        this.isAuthenticated = false;
        this.partnerAuthSession = null;
        this.otpSession = null;
        this.phoneAuthSession = null;
        this.lastLoginMethod = null;
        this.sessionExpiresAt = null;
      });

      // Clear persisted data
      this.clearPersistedAuthTokens();
      this.clearPersistedAuthSession();
      this.clearAnonymousSession();

      // Call original sign out if needed
      if (this.authenticateUserUseCase) {
        // await this.authenticateUserUseCase.signOut();
      }
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to sign out'
      );
    } finally {
      this.setSigningOut(false);
    }
  }

  // Error management
  addError(error: AuthError): void {
    runInAction(() => {
      this.errors.push(error);
    });
  }

  clearErrors(): void {
    runInAction(() => {
      this.errors = [];
      this.error = null;
    });
  }

  removeError(index: number): void {
    runInAction(() => {
      this.errors.splice(index, 1);
    });
  }

  // Session management
  setRememberMe(remember: boolean): void {
    this.rememberMe = remember;
  }

  // Validation helpers
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhoneNumber(phone: string): boolean {
    // Ghana phone number format
    const phoneRegex =
      /^(\+233|0)(2[0-9]|5[0-4]|24|54|27|57|26|56|23|28)[0-9]{7}$/;
    return phoneRegex.test(phone);
  }

  formatPhoneNumber(phone: string): string {
    // Convert to international format
    if (phone.startsWith('0')) {
      return '+233' + phone.substring(1);
    }
    if (!phone.startsWith('+233')) {
      return '+233' + phone;
    }
    return phone;
  }

  // Session expiry management
  checkSessionExpiry(): boolean {
    if (this.sessionExpiresAt && new Date() > this.sessionExpiresAt) {
      this.handleSessionExpiry();
      return true;
    }
    return false;
  }

  extendSession(): void {
    runInAction(() => {
      this.sessionExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);
      this.isSessionExpiring = false;
    });
  }

  // Registration data management
  updateRegistrationData(data: Partial<RegistrationData>): void {
    runInAction(() => {
      this.registrationData = { ...this.registrationData, ...data };
    });

    // Persist to session storage for cross-component access
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(
        'registrationData',
        JSON.stringify(this.registrationData)
      );
    }
  }

  clearRegistrationData(): void {
    runInAction(() => {
      this.registrationData = {};
    });

    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('registrationData');
    }
  }

  loadRegistrationData(): void {
    if (typeof window !== 'undefined') {
      const storedData = sessionStorage.getItem('registrationData');
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          runInAction(() => {
            this.registrationData = parsedData;
          });
        } catch (_error) {
          // Ignore invalid cached registration payloads.
        }
      }
    }
  }

  signOut() {
    runInAction(() => {
      this.currentUser = null;
      this.isAuthenticated = false;
      this.authTokens = null;
      this.sessionExpiresAt = null;
      this.lastLoginMethod = null;
    });
    localStorage.clear();
  }

  private async checkServerSession(): Promise<boolean> {
    if (typeof window === 'undefined') return false;
    try {
      const response = await fetch('/api/auth/session', { cache: 'no-store' });
      if (!response.ok) return false;
      const data = (await response.json()) as { authenticated?: boolean };
      return Boolean(data.authenticated);
    } catch {
      return false;
    }
  }

  private async refreshServerSession(): Promise<boolean> {
    if (typeof window === 'undefined') return false;
    try {
      const response = await fetch('/api/auth/refresh', { method: 'POST' });
      return response.ok;
    } catch {
      return false;
    }
  }

  private async setServerSessionCookies(payload: {
    access_token: string;
    refresh_token?: string;
    partner_code?: string;
    expires_in?: number;
  }): Promise<boolean> {
    if (typeof window === 'undefined') return false;
    try {
      const response = await fetch('/api/auth/set-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  private persistAuthTokens(tokens: {
    accessToken: string;
    refreshToken: string;
  }): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    }
  }

  private getPersistedAuthTokens(): {
    accessToken: string;
    refreshToken: string;
  } | null {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('auth_tokens');
      return stored ? JSON.parse(stored) : null;
    }
    return null;
  }

  private clearPersistedAuthTokens(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_tokens');
    }
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  private setSigningIn(signingIn: boolean): void {
    this.isSigningIn = signingIn;
  }

  private setSigningOut(signingOut: boolean): void {
    this.isSigningOut = signingOut;
  }

  private setError(error: string | null): void {
    this.error = error;
  }

  private persistAuthSession(session: {
    user: any;
    expiresAt: Date;
    method: 'email' | 'phone' | 'partner' | 'otp';
  }): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(
        'auth_session',
        JSON.stringify({
          ...session,
          expiresAt: session.expiresAt.toISOString(),
        })
      );
    }
  }

  private getPersistedAuthSession(): {
    user: User;
    expiresAt: Date;
    method: 'email' | 'phone' | 'partner' | 'otp';
  } | null {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('auth_session');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...parsed,
          expiresAt: new Date(parsed.expiresAt),
        };
      }
    }
    return null;
  }

  private clearPersistedAuthSession(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_session');
    }
  }

  private handleSessionExpiry(): void {
    runInAction(() => {
      this.isSessionExpiring = true;
    });

    // Auto sign out after 5 minutes warning
    setTimeout(() => {
      if (this.isSessionExpiring) {
        // this.signOut();
      }
    }, 5 * 60 * 1000);
  }

  // Enhanced initialization
  private async initializeAuth(): Promise<void> {
    this.setLoading(true);

    try {
      const storedSession = this.getPersistedAuthSession();

      // Prefer showing stored user info (while we reconcile with server cookies).
      if (storedSession) {
        runInAction(() => {
          this.currentUser = storedSession.user;
          this.isAuthenticated = new Date() < storedSession.expiresAt;
          this.sessionExpiresAt = storedSession.expiresAt;
          this.lastLoginMethod = storedSession.method;
        });
      }

      // Reconcile with server-side cookie session (middleware relies on access_token cookie).
      const serverAuthenticated = await this.checkServerSession();
      if (serverAuthenticated) {
        runInAction(() => {
          this.isAuthenticated = true;
        });
        return;
      }

      // If access token is expired/missing but refresh_token cookie exists, refresh first
      // (prevents “logged-in header but middleware redirects” after token rotation).
      const refreshed = await this.refreshServerSession();
      if (refreshed) {
        const serverAuthenticatedAfterRefresh = await this.checkServerSession();
        if (serverAuthenticatedAfterRefresh) {
          runInAction(() => {
            this.isAuthenticated = true;
          });
          return;
        }
      }

      // Attempt to bootstrap server cookies from stored access token (covers legacy sessions
      // where auth succeeded but cookies were path-scoped or auth calls bypassed the proxy).
      if (typeof window !== 'undefined') {
        const storedTokens = this.getPersistedAuthTokens();
        const accessToken =
          window.localStorage.getItem('motito_auth_token') ??
          storedTokens?.accessToken ??
          '';
        const refreshToken = storedTokens?.refreshToken ?? '';

        if (accessToken) {
          await this.setServerSessionCookies({
            access_token: accessToken,
            refresh_token: refreshToken || undefined,
          });
        }
      }

      const serverAuthenticatedAfter = await this.checkServerSession();
      if (!serverAuthenticatedAfter) {
        // Server still doesn't recognize the session; force a clean sign-out so UI
        // doesn't show an authenticated header while middleware redirects.
        this.clearPersistedAuthTokens();
        this.clearPersistedAuthSession();
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('motito_auth_token');
          window.localStorage.removeItem('motito_auth_expiry');
        }
        runInAction(() => {
          this.currentUser = null;
          this.isAuthenticated = false;
          this.sessionExpiresAt = null;
          this.lastLoginMethod = null;
        });
      } else {
        runInAction(() => {
          this.isAuthenticated = true;
        });
      }
    } finally {
      runInAction(() => {
        this.isHydrated = true;
        this.isLoading = false;
      });
    }
  }
}

export interface AuthError {
  field?: string;
  message: string;
  code?: string;
}

export interface LoginCredentials {
  email?: string;
  phone?: string;
  password: string;
}

export interface PhoneAuthSession {
  phoneNumber: string;
  sessionId?: string;
  otpSent: boolean;
  otpVerified: boolean;
  expiresAt: Date;
  attempts: number;
  maxAttempts: number;
}
export interface LoginAuthSession {
  user:      User;
  expiresAt: Date;
  method:    string;
}

export interface User {
  id:              string;
  username:        string;
  account_type:    string;
  first_name:      string;
  last_name:       string;
  // Contact info - returned at root level by backend
  email:           string | null;
  phone_number:    string | null;
  email_verified:  boolean;
  msisdn_verified: boolean;
  // Other fields
  kyc_level:       string;
  account_status:  string;
  permissions:     string[];
  roles:           Role[];
  // Optional nested objects (may not always be present)
  identity?:       Identity;
  profile?:        Profile;
  primary_role?:   Role | null;
  partner_org_code?: string;
}

export interface Identity {
  id:             string;
  email:          string;
  email_verified: boolean;
  phone:          string;
  phone_verified: boolean;
}

export interface Profile {
  first_name:    string;
  last_name:     string;
  date_of_birth: Date;
  kyc_level:     string;
}

export interface Role {
  code: string;
  name: string;
}



export interface PartnerAuthSession {
  sessionToken: string;
  userStatus: 'new_user' | 'existing_user';
  missingFields?: string[];
  autoLoginAvailable: boolean;
  requiresOtp: boolean;
  requiresOtpReason?: string;
  userData: any;
  partnerCode: string;
  campaignId: string;
  expiresAt: Date;
}

export interface OTPSession {
  msisdn: string;
  requestId: string;
  purpose: 'SIGNUP' | 'LOGIN';
  expiresAt: Date;
  attempts: number;
  maxAttempts: number;
}

export interface RegistrationData {
  whatsappSameNumber?: boolean;
  phone?: string;
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  dateOfBirth?: string;
  gender?: string;
  maritalStatus?: string;
  occupation?: string;
  company?: string;
  salary?: string;
  digitalAddress?: string;
  residentialAddress?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  aboutUs?: string;
  merchant?: string;
  acceptsMarketing?: boolean;
  otpCode?: string; // Verified OTP code for final registration
  employmentStatus?: string;
}
